<template>
    <div class="db-task-summary">
        <div class="row">
            <div class="col-md-12">
                <div class="card">
                    <div class="card-header text-black p-2">
                        <div class="d-flex justify-content-between align-items-center">
                            <div class="d-flex gap-1">
                                <SquareArrowLeft
                                    :size="24"
                                    :stroke-width="2"
                                    class="text-theme cursor-pointer"
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="top"
                                    data-bs-title="Previous Week"
                                    @click="prepareDateRange('prev')"
                                />
                                <SquareArrowRight
                                    :size="24"
                                    :stroke-width="2"
                                    class="text-theme cursor-pointer"
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="top"
                                    data-bs-title="Next Week"
                                    @click="prepareDateRange('next')"
                                />
                                <div class="ms-2">
                                    Handyman Task Summary ({{ dataTimeFormat(startOfWeek, 'DD MMM') }} - {{ dataTimeFormat(endOfWeek, 'DD MMM, YYYY') }})
                                </div>
                            </div>
                            <div class="d-flex gap-1 align-items-center">
                                <div>
                                    <input type="text" class="form-control form-control-sm" v-model="search" placeholder="Search" />
                                </div>
                                <Download
                                    :size="24"
                                    :stroke-width="2"
                                    class="text-theme cursor-pointer"
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="top"
                                    data-bs-title="Export to CSV"
                                    @click="exportToCSV"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="card-body p-2">
                        <div class="table-responsive">
                            <table class="table hm-fs-12 hm-table-fixed table-hover table-sm mb-0">
                                <thead>
                                    <tr>
                                        <th
                                            class="text-center bg-theme"
                                            v-for="(week, index) in weekDays"
                                            :key="index"
                                        >
                                            {{ week.day }}
                                            <div class="fs-6">
                                                {{ week.day_of_month }}/{{ week.year }}
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="isLoadingHandymanTasks">
                                        <td colspan="6" class="text-center">Processing...</td>
                                    </tr>
                                    <tr v-else-if="hasBookings">
                                        <td v-for="(week, index) in weekDays" :key="index" class="p-0">
                                            <template v-for="(user, userIndex) in filterdData">
                                                <div
                                                    v-if="userHasOrdersOnDate(user.bookings, week.date)"
                                                    class="user-orders p-2 text-black"
                                                    :class="[$cardRandomBgClass()]"
                                                >
                                                    <div class="fw-bold pb-1 text-capitalize">{{ user?.name }}</div>
                                                    <!-- Display the order -->
                                                    <div
                                                        v-if="Object.keys(user.bookings).length > 0"
                                                        v-for="(order, index) in getOrdersForDate(user.bookings, week.date)"
                                                        class="hm-fs-9"
                                                        :class="{'mb-2 border-bottom border-theme':index < Object.keys(getOrdersForDate(user.bookings, week.date)).length - 1}"
                                                    >
                                                        <div 
                                                            @click="openBooking(order)" 
                                                            class="cursor-pointer"
                                                            :class="{'mb-1':index < Object.keys(getOrdersForDate(user.bookings, week.date)).length - 1}"
                                                        >
                                                            <div class="d-flex justify-content-between gap-2">
                                                                <span>{{ order.service_time }}</span>
                                                                <span class="text-end text-uppercase">{{ order?.postal_code }}</span>
                                                                <!-- <label class="text-capitalize text-end">
                                                                    <template v-if="Object.keys(order.order_items).length > 0">
                                                                        {{ order.order_items.map(service => service.service.name).join(', ') }}
                                                                    </template>
                                                                </label> -->
                                                            </div>
                                                            <div class="d-flex justify-content-between">
                                                                <span class="text-capitalize">{{ (typeof order.customer_name !== 'undefined' ? $truncateString(order?.customer_name, 20) : '') }}</span>
                                                                <!-- <label class="text-end text-uppercase">{{ order.address.postal_code }}</label> -->
                                                                <span class="text-capitalize text-end">
                                                                    <template v-if="Object.keys(order.services).length > 0">
                                                                        {{ $truncateString(order.services.map(service => service?.service_name).join(', '), 15) }}
                                                                    </template>
                                                                    <template v-else>
                                                                        {{ $truncateString(order.project_name, 15) }}
                                                                    </template>
                                                                </span>
                                                            </div>
                                                            <div class="d-flex justify-content-between">
                                                                <span class="badge bg-theme text-capitalize">{{ order.type }}</span>
                                                                <span class="badge bg-theme text-capitalize text-end">{{ order.status.replace('_', ' ') }}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-else>
                                        <td colspan="6" class="text-center">No task has been added in the system.</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { appMixin } from '../../mixin/appMixin';
import {
    SquareArrowRight,
    SquareArrowLeft,
    Download
} from 'lucide-vue-next';
export default {
    name: 'TaskSummary',
    mixins: [appMixin],
    components: {
        SquareArrowRight,
        SquareArrowLeft,
        Download
    },
    data() {
        return {
            handymanTasks: [],
            isLoadingHandymanTasks: true,
            currentWeek: '',
            startOfWeek: '',
            endOfWeek: '',
            weekDays: [],
            search: '',
        }
    },
    created() {
        this.startOfWeek = this.moment().startOf('isoWeek');
        this.endOfWeek = this.moment().endOf('isoWeek');
        
        // Prepare current week
        this.currentWeek = [this.startOfWeek, this.endOfWeek];
    },
    mounted() {
        this.getStats();
        // Prepare current week
        this.preparedWeekDays();

        // Listen for schedule time changed event
        this.$emitter.on('scheduleTimeChanged', () => {
            this.getStats();
        });

        this.$emitter.on('refresh-order-list', (data) => {
            this.getStats();
        });
    },
    computed: {
        filterdData() {
            const query = this.search.toLowerCase().trim();
            if (!query) return this.handymanTasks;
            
            return this.handymanTasks
                .filter(handyman => {
                    // First check if handyman name matches
                    const isHandymanMatch = handyman.name.toLowerCase().includes(query);
                    
                    // If handyman name matches, include all their bookings
                    if (isHandymanMatch) return true;
                    
                    // Otherwise, check if any booking matches
                    return handyman.bookings.some(order => 
                        order?.service_address?.toLowerCase()?.includes(query) ||
                        order?.services?.some(item =>
                            item.service_name?.toLowerCase()?.includes(query)
                        ) ||
                        order?.customer_name?.toLowerCase()?.includes(query) ||
                        order?.customer_email?.toLowerCase()?.includes(query) ||
                        order?.customer_phone?.includes(query) ||
                        order?.postal_code?.toLowerCase()?.includes(query) ||
                        order?.order_number?.toString()?.toLowerCase()?.includes(query) ||
                        order?.type?.toLowerCase()?.includes(query) ||
                        order?.status?.toLowerCase()?.includes(query)
                    );
                })
                .map(handyman => {
                    // If handyman name matches, return all their bookings
                    if (handyman.name.toLowerCase().includes(query)) {
                        return handyman;
                    }
                    
                    // Otherwise, filter their bookings to only include matching ones
                    return {
                        ...handyman,
                        bookings: handyman.bookings.filter(order => 
                            order?.service_address?.toLowerCase()?.includes(query) ||
                            order?.services?.some(item =>
                                item.service_name?.toLowerCase()?.includes(query)
                            ) ||
                            order?.customer_name?.toLowerCase()?.includes(query) ||
                            order?.customer_email?.toLowerCase()?.includes(query) ||
                            order?.customer_phone?.includes(query) ||
                            order?.postal_code?.toLowerCase()?.includes(query) ||
                            order?.order_number?.toString()?.toLowerCase()?.includes(query) ||
                            order?.type?.toLowerCase()?.includes(query) ||
                            order?.status?.toLowerCase()?.includes(query)
                        )
                    };
                });
        },
        hasBookings() {
            return this.filterdData.some(handyman => handyman.bookings.length > 0);
        }
    },
    watch: {
        currentWeek(newVal) {
            console.log(newVal);
            this.startOfWeek = this.moment(newVal[0]);
            this.endOfWeek = this.moment(newVal[1]);

            this.getStats();
            this.preparedWeekDays();

        }
    },
    methods: {
        getStats() {
            let self = this;
            // Format dates as YYYY-MM-DD to avoid timezone issues
            const formattedStartDate = this.moment(this.startOfWeek).format('YYYY-MM-DD');
            const formattedEndDate = this.moment(this.endOfWeek).format('YYYY-MM-DD');
            
            axios.post('/admin/dashboard/stats?action=handyman_tasks', {
                action: 'handyman_tasks',
                start_date: formattedStartDate,
                end_date: formattedEndDate
            })
                .then(response => {
                    // Get order summary
                    self.handymanTasks = response.data;
                    self.isLoadingHandymanTasks = false;
                });
        },
        preparedWeekDays() {
            // Prepare week days
            const startDate = this.startOfWeek; // Start of the current week
            const endDate = this.endOfWeek; // End of the current week

            const currentDay = this.moment(startDate);
            const weekDays = [];

            while (currentDay.isSameOrBefore(endDate)) {
                const dayOfWeek = currentDay.isoWeekday(); // isoWeekday returns 1 for Monday and 7 for Sunday
                if (dayOfWeek >= 1 && dayOfWeek <= 6) {
                    weekDays.push({
                        year: currentDay.format("YY"),
                        date: currentDay.format("YYYY-MM-DD"),
                        day: currentDay.format("ddd"),
                        day_of_month: currentDay.format("DD"),
                    });
                }
                currentDay.add(1, "day");
            }

            this.weekDays = weekDays;
        },
        // Filters the orders for a specific user based on the date
        getOrdersForDate(orders, date) {
            return orders.filter(order => this.$dateTimeFormat(order.service_date, 'YYYY-MM-DD') === date);
        },
        // Checks if the user has any orders on a specific date
        userHasOrdersOnDate(orders, date) {
            return orders.some(order => this.$dateTimeFormat(order.service_date, 'YYYY-MM-DD') === date);
        },
        // Method to filter users for the given date
        getUsersForDay(date) {
            return this.handymanTasks.filter(user => {
                return user.bookings.some(order => this.$dateTimeFormat(order.service_date, 'YYYY-MM-DD') === date);
            });
        },
        prepareDateRange(action) {
            if (action === 'prev') {
                this.startOfWeek = this.moment(this.startOfWeek).subtract(1, 'week');
                this.endOfWeek = this.moment(this.endOfWeek).subtract(1, 'week');
            } else if (action === 'next') {
                this.startOfWeek = this.moment(this.startOfWeek).add(1, 'week');
                this.endOfWeek = this.moment(this.endOfWeek).add(1, 'week');
            }
            this.getStats();
            this.preparedWeekDays();
        },
        openBooking(order) {
            if(order.type == 'project') {
                this.$goToPage(`/admin/projects/${order.project_id}`)
            } else {
                this.$goToPage(`/admin/orders/${order.order_number}`)
            }
        },
        exportToCSV() {
            // CSV header
            const headers = [
                'Handyman Name',
                'Service Name',
                'Address',
                'Customer Name',
                'Customer Phone',
                'Customer Email',
                'Service Date',
                'Service Time',
                'Duration (hours)',
                'Order Number',
                'Order Type'
            ];

            let csv = headers.join(',') + '\n';

            this.filterdData.forEach(user => {
                user.bookings.forEach(order => {
                    let duration = order.type == 'project' ? 'Full Day' : order.oh_service_hours;
                    const row = [
                        `"${user.name}"`,
                        `"${this.prepareServiceName(order.services)}"`,
                        `"${order.service_address}"`,
                        `"${order.customer_name}"`,
                        `"${order.customer_phone}"`,
                        `"${order.customer_email}"`,
                        `"${this.dataTimeFormat(order.service_date, 'YYYY-MM-DD')}"`,
                        `"${order.service_time}"`,
                        `"${duration}"`,
                        `"${order.order_number || order.project_name}"`,
                        `"${this.capitalize(order.type.replace('_', ' '))}"`
                    ];
                    csv += row.join(',') + '\n';
                });
            });

            // Create and trigger download
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'handyman_tasks.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        prepareServiceName(items) {
            return items.map(item => item.service_name).join(', ');
        }
    }
}
</script>
